import moment from 'moment';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAxios } from '../../utils/hook/useAxios';
import PhotosGallery from '../PhotosGallery';

/*
 * Styles : mc-inbox.css (importé par la page de messagerie).
 */

const STATUS_BADGES = {
  pending: { label: 'En attente de validation', tone: 'warn' },
  accepted: { label: 'Acceptée', tone: 'ok' },
  rejected: { label: 'Refusée', tone: 'danger' },
  checkin_pending_validation: { label: 'Check-in en attente', tone: 'warn' },
  checked_in: { label: 'Location en cours', tone: 'info' },
  in_progress: { label: 'Location en cours', tone: 'info' },
  completed: { label: 'Location terminée', tone: 'ok' },
  dispute_open: { label: 'Litige ouvert', tone: 'danger' },
};

const hasContent = (obj) => Boolean(obj) && Object.keys(obj).length > 0;

function Row({ label, children, danger }) {
  return (
    <div className={`mc-ib-kv${danger ? ' mc-ib-kv--danger' : ''}`}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function ReservationOwnerPanel({ reservation, onReservationUpdated }) {
  const [showPhotos, setShowPhotos] = useState(false);
  const [showCompare, setShowCompare] = useState(false);

  const [checkinData, setCheckinData] = useState({});
  const [checkoutData, setCheckoutData] = useState({});
  const [extraPreview, setExtraPreview] = useState(null);
  const [showCheckoutConfirm, setShowCheckoutConfirm] = useState(false);
  const [lastExtraCharge, setLastExtraCharge] = useState(null);

  const checkin = reservation?.checkin ?? true;
  const checkout = reservation?.checkout ?? true;
  const axios = useAxios();

  useEffect(() => {
    let fetchReviews = async () => {
      try {
        const response = await axios.get('/checkin/' + reservation.reservationId);
        if (response.data) {
          setCheckinData(response.data);
        }
      } catch (error) {
        console.error('Erreur lors de la requête Axios :', error);
      }
    };
    fetchReviews();
  }, []);

  useEffect(() => {
    let fetchReviews = async () => {
      try {
        const response = await axios.get('/checkout/' + reservation.reservationId);
        if (response.data) {
          setCheckoutData(response.data);
        }
      } catch (error) {
        console.error('Erreur lors de la requête Axios :', error);
      }
    };
    fetchReviews();
  }, []);

  // Fetch extra distance preview when checkout data is ready
  useEffect(() => {
    if (!checkoutData?.kmEnd) return;
    axios
      .get('/checkout/' + reservation.reservationId + '/extra-distance-preview')
      .then((r) => setExtraPreview(r.data))
      .catch(() => {});
  }, [checkoutData?.kmEnd]);

  // Fermeture de la modale avec Échap
  useEffect(() => {
    if (!showCheckoutConfirm) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setShowCheckoutConfirm(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showCheckoutConfirm]);

  const handleValidateCheckin = async () => {
    try {
      // Upload toutes les images check-in

      const { data } = await axios.put('/checkin/' + reservation.reservationId + '/validate');
      if (data) {
        toast.success('Validation effectuée avec succès');
        onReservationUpdated?.(); // 🔥 recharge les données
      }
    } catch (error) {
      console.error('Erreur check-in :', error.response?.data || error.message);
    }
  };

  const handleRequestCheckoutConfirm = () => {
    setShowCheckoutConfirm(true);
  };

  const handleValidateCheckout = async () => {
    setShowCheckoutConfirm(false);
    try {
      const { data } = await axios.put('/checkout/' + reservation.reservationId + '/validate');
      if (data) {
        if (data.extraCharge?.charged) {
          setLastExtraCharge(data.extraCharge);
          toast.success(
            `Checkout validé — ${data.extraCharge.amount.toFixed(2)} € prélevés pour les km supplémentaires`
          );
        } else {
          toast.success('Validation effectuée avec succès');
        }
        onReservationUpdated?.();
        axios
          .put('/reservationsGain/' + reservation.reservationId, { type: '2' })
          .catch((e) => console.log(e));
      }
    } catch (error) {
      console.error('Erreur checkout :', error.response?.data || error.message);
      toast.error('Erreur lors de la validation');
    }
  };

  const model = reservation?.Vehicle?.VehicleModel;
  const badge = STATUS_BADGES[reservation?.status];
  const hasCheckin = hasContent(checkinData);
  const hasCheckout = hasContent(checkoutData);

  const kmOut = parseFloat(checkoutData?.kmEnd);
  const kmIn = parseFloat(checkinData?.kmStart);
  const dist = kmOut - kmIn;

  return (
    <div className="mc-ib-resa">
      {/* HEADER */}
      <div className="mc-ib-resa-head">
        {reservation?.Vehicle?.principalPhotos ? (
          <img
            src={reservation.Vehicle.principalPhotos}
            alt={`${model?.marque || ''} ${model?.modele || 'Véhicule'}`.trim()}
            className="mc-ib-resa-photo"
          />
        ) : (
          <div className="mc-ib-resa-photo mc-ib-resa-photo--empty" aria-hidden="true">
            <i className="bi bi-car-front"></i>
          </div>
        )}

        <div>
          <h3 className="mc-ib-resa-title">
            {model?.marque} {model?.modele}
          </h3>
          <p className="mc-ib-resa-meta">
            Conducteur : {reservation?.Invite?.firstName} {reservation?.Invite?.lastName}
            <br />
            {moment(reservation?.startDate).format('DD MMM HH:mm')} →{' '}
            {moment(reservation?.endDate).format('DD MMM HH:mm')}
          </p>
        </div>
      </div>

      {/* STATUS BADGE */}
      {badge && (
        <div className="mc-ib-resa-badges">
          <span className={`mc-ib-chip mc-ib-chip--${badge.tone}`}>{badge.label}</span>
        </div>
      )}

      {/* SECTION CHECK-IN */}
      {hasCheckin && (
        <section className="mc-ib-section" aria-labelledby="resa-checkin-title">
          <h4 className="mc-ib-section-title" id="resa-checkin-title">
            <i className="bi bi-geo-alt" aria-hidden="true"></i>
            Check-in (départ)
          </h4>

          <dl className="mc-ib-kv-list">
            <Row label="Km départ">{checkinData.kmStart ?? '—'}</Row>
            <Row label="Carburant">{checkinData.fuelStart ?? '—'}</Row>
          </dl>

          <div className="mc-ib-section-actions">
            <button
              type="button"
              className="mc-btn mc-btn--secondary mc-btn--sm"
              onClick={() => setShowPhotos(!showPhotos)}
              aria-expanded={showPhotos}
            >
              <i className="bi bi-images" aria-hidden="true"></i>
              {showPhotos ? 'Masquer les photos' : 'Voir photos départ'}
            </button>

            <button
              type="button"
              className="mc-btn mc-btn--primary mc-btn--sm"
              onClick={handleValidateCheckin}
            >
              <i className="bi bi-check-lg" aria-hidden="true"></i>
              Valider le check-in
            </button>
          </div>

          {showPhotos && (
            <div className="mc-ib-gallery">
              <PhotosGallery photos={checkinData.photos} />
            </div>
          )}
        </section>
      )}

      {/* LOCATION EN COURS */}
      {reservation?.status === 'checked_in' && (
        <div className="mc-ib-running">
          Location en cours jusqu’au{' '}
          <strong>{moment(reservation.endDate).format('DD MMM HH:mm')}</strong>
          <div className="mc-ib-section-actions">
            <button type="button" className="mc-btn mc-btn--secondary mc-btn--sm">
              Contacter conducteur
            </button>
            <button type="button" className="mc-btn mc-btn--danger-soft mc-btn--sm">
              Signaler un problème
            </button>
          </div>
        </div>
      )}

      {/* CHECKOUT */}
      {hasCheckout && (
        <section className="mc-ib-section" aria-labelledby="resa-checkout-title">
          <h4 className="mc-ib-section-title" id="resa-checkout-title">
            <i className="bi bi-flag" aria-hidden="true"></i>
            Véhicule retourné
          </h4>

          <dl className="mc-ib-kv-list">
            <Row label="Km retour">{checkoutData.kmEnd ?? '—'}</Row>
            <Row label="Distance parcourue">
              {!isNaN(dist) && dist >= 0 ? (
                `${dist.toLocaleString('fr-FR')} km`
              ) : (
                <span className="text-muted">
                  Non disponible
                  {!checkoutData.kmEnd && ' (km retour non renseigné)'}
                  {!checkinData.kmStart && ' (km départ non renseigné)'}
                </span>
              )}
            </Row>
            <Row label="Carburant">{checkoutData.fuel_end ?? '—'}</Row>
          </dl>

          {checkoutData.comment && <p className="mc-ib-note">“{checkoutData.comment}”</p>}

          {/* COMPARATEUR (bouton de bascule désactivé dans la version d'origine) */}
          {showCompare && (
            <div className="mc-ib-compare">
              <div>
                <p>Avant</p>
                {checkinData.photos &&
                  checkinData.photos.map((p, i) => <img key={i} src={p} alt="" />)}
              </div>
              <div>
                <p>Après</p>
                {checkoutData.photos &&
                  checkoutData.photos.map((p, i) => <img key={i} src={p} alt="" />)}
              </div>
            </div>
          )}

          {/* ── Aperçu km supplémentaires ── */}
          {extraPreview && (
            <div className={`mc-ib-km${extraPreview.hasExtra ? ' mc-ib-km--extra' : ''}`}>
              <p className="mc-ib-km-title">
                <i
                  className={`bi ${extraPreview.hasExtra ? 'bi-exclamation-triangle-fill' : 'bi-check-circle-fill'}`}
                  aria-hidden="true"
                ></i>
                Bilan kilométrique
              </p>
              <dl className="mc-ib-kv-list">
                <Row label="Km départ">{extraPreview.kmStart?.toLocaleString('fr-FR')} km</Row>
                <Row label="Km retour">{extraPreview.kmEnd?.toLocaleString('fr-FR')} km</Row>
                <Row label="Distance parcourue">
                  {extraPreview.distanceParcourue?.toLocaleString('fr-FR')} km
                </Row>
                <Row label="Distance incluse">
                  {extraPreview.freeKm?.toLocaleString('fr-FR')} km
                </Row>
                {extraPreview.hasExtra && (
                  <>
                    <Row label="Km supplémentaires" danger>
                      {extraPreview.kmExcess?.toLocaleString('fr-FR')} km
                    </Row>
                    <Row label={`Surcharge (${extraPreview.pricePerKm}€/km)`} danger>
                      {extraPreview.extraAmount?.toFixed(2)} €
                    </Row>
                  </>
                )}
              </dl>
              {!extraPreview.hasExtra && extraPreview.kmExcess === 0 && (
                <p className="mc-ib-km-ok">Aucun km supplémentaire</p>
              )}
            </div>
          )}

          {/* Résultat du dernier paiement extra */}
          {lastExtraCharge && (
            <div className="mc-ib-charged">
              <i className="bi bi-credit-card" aria-hidden="true"></i>
              <span>
                <strong>{lastExtraCharge.amount.toFixed(2)} €</strong> prélevés pour{' '}
                {lastExtraCharge.kmExcess} km supplémentaires.
              </span>
            </div>
          )}

          <div className="mc-ib-section-actions">
            <button type="button" className="mc-btn mc-btn--danger-soft mc-btn--sm">
              Signaler dommage
            </button>
            <button
              type="button"
              className="mc-btn mc-btn--primary mc-btn--sm"
              onClick={handleRequestCheckoutConfirm}
            >
              Valider le checkout
            </button>
          </div>
        </section>
      )}

      {/* ── Modal confirmation checkout ── */}
      {showCheckoutConfirm && (
        <div
          className="mc-ib-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCheckoutConfirm(false);
          }}
        >
          <div
            className="mc-ib-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkout-confirm-title"
          >
            <div className="mc-ib-modal-head">
              <h5 className="mc-ib-modal-title" id="checkout-confirm-title">
                Confirmer la validation
              </h5>
              <button
                type="button"
                className="mc-ib-icon-btn"
                aria-label="Fermer"
                onClick={() => setShowCheckoutConfirm(false)}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="mc-ib-modal-body">
              {extraPreview?.hasExtra ? (
                <div className="mc-ib-alert">
                  <i className="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>
                  <span>
                    <strong>{extraPreview.kmExcess} km supplémentaires</strong> seront facturés au
                    conducteur.
                    <br />
                    <strong>
                      {extraPreview.extraAmount?.toFixed(2)} € prélevés automatiquement.
                    </strong>
                  </span>
                </div>
              ) : (
                <p>Aucun km supplémentaire détecté. La location sera clôturée sans surcharge.</p>
              )}
              <p className="text-muted small">Cette action est irréversible.</p>
            </div>

            <div className="mc-ib-modal-foot">
              <button
                type="button"
                className="mc-btn mc-btn--ghost mc-btn--sm"
                onClick={() => setShowCheckoutConfirm(false)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="mc-btn mc-btn--primary mc-btn--sm"
                onClick={handleValidateCheckout}
              >
                Confirmer et valider
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FIN LOCATION */}
      {reservation?.status === 'completed' && (
        <div className="mc-ib-done">
          <i className="bi bi-check-circle-fill" aria-hidden="true"></i>
          Location terminée et clôturée
        </div>
      )}
    </div>
  );
}

export default ReservationOwnerPanel;
